import React from 'react';
import moment from 'moment';
import { withRouter } from 'react-router';
import { connect } from 'react-redux';
import { PropTypes } from 'prop-types';
import { getWhdStatement, changeImyTierParent, setStatementPosition, hideOwnershipModal,
  showAccountWithholdingModal, takeQueueItem,
  hideAccountWithholdingModal,
  addForm, showAddFormModal,
  hideAddFormModal, addNewBeneficialOwner, saveCollapsedTiers,
  hidePooledRatesModal, showPooledRatesModal } from 'actions';
import { history } from 'containers/App';
import removeInvalidHtmlChars from 'common/App/removeInvalidHtmlChars';
import StatementTypes from 'common/Forms/StatementTypes';
import ImyChapter3Status from 'common/Forms/ImyChapter3Status';
import Chapter3Status from 'common/Customers/Chapter3Status';
import Chapter4Status from 'common/Customers/Chapter4Status';
import ValidationInspector from 'common/Validation/ValidationInspector';
import AccessControl from 'components/AccessControl';
import ActivityPerms from 'components/AccessControl/permissions.json';
import withContainerErrorBoundary from 'components/ErrorBoundaries/ContainerErrorBoundary/withContainerErrorBoundary';
import BeneficialOwner from 'components/WhdStatement/beneficialOwner';
import IntermediaryDetailsPanel from 'components/WhdStatement/intermediaryDetailsPanel';
import LoadingWrapper from 'components/Common/LoadingWrapper';
import NotFoundPage from 'components/Errors/NotFoundPage';
import ChangeOwnershipModal from 'components/Modals/ChangeOwnershipModal';
import AddFormModal from 'components/Modals/AddFormModal';
import WithholdingRatesModal from 'components/Modals/WithholdingRatesModal';
import PooledRatesModal from 'components/Modals/PooledRatesModal';
import StatementOptionsPanel from 'components/WhdStatement/statementOptionsPanel';
import StatementActionsPanel from 'components/WhdStatement/statementActionsPanel';
import ModalWrapper from 'components/Modals/ModalWrapper';
import SimpleAutoComplete from 'components/Common/Inputs/SimpleAutoComplete';
import LinkWhsAccountModal from 'components/Modals/LinkWhsAccountModal';
import Banner from 'components/Common/Banner';

class WhdStatement extends React.PureComponent {
  static propTypes = {
    dispatch: PropTypes.func.isRequired,
    isGettingStatement: PropTypes.bool,
    isUpdatingStatement: PropTypes.bool,
    isStructureValid: PropTypes.bool,
    match: PropTypes.shape({
      params: PropTypes.shape({
        accountId: PropTypes.string.isRequired,
        versionNumber: PropTypes.string
      }).isRequired
    }).isRequired,
    beneficiaryList: PropTypes.arrayOf(PropTypes.shape({})),
    permissions: PropTypes.arrayOf(PropTypes.string),
    settings: PropTypes.shape({
      showMultiTierManager: PropTypes.bool,
      showWithholding: PropTypes.bool,
      selectedClient: PropTypes.shape({
        isPiiProtectionEnabled: PropTypes.bool
      })
    }),
    statement: PropTypes.shape({
      beneficialOwners: PropTypes.arrayOf(PropTypes.shape({})),
      decimalPlacePrecision: PropTypes.number,
      intermediaryName: PropTypes.string,
      accounts: PropTypes.arrayOf(PropTypes.shape({})),
      type: PropTypes.number,
      formId: PropTypes.number,
      effectiveTo: PropTypes.string,
      jointAccountElection: PropTypes.string
    }),
    structure: PropTypes.shape({
      basicDetails: PropTypes.shape({
        formId: PropTypes.number,
        accountId: PropTypes.number,
        name: PropTypes.string,
        formIsValid: PropTypes.bool,
        workflowStage: PropTypes.number,
      }),
      recipients: PropTypes.arrayOf(PropTypes.shape({})),
    }),
    statementPosition: PropTypes.shape({}),
    isOwnershipModalShown: PropTypes.bool,
    changeOwnershipItem: PropTypes.shape(),
    isAccountWithholdingShown: PropTypes.bool.isRequired,
    isAddFormModalShown: PropTypes.bool.isRequired,
    isSavingStatement: PropTypes.bool,
    validationResult: PropTypes.shape({
      lines: PropTypes.arrayOf(PropTypes.shape({})),
      rejections: PropTypes.arrayOf(PropTypes.shape({})),
      warnings: PropTypes.arrayOf(PropTypes.shape({})),
    }),
    tierCollapseStructure: PropTypes.shape({}),
    isPooledRatesModalShown: PropTypes.bool.isRequired,
    isDeletingRow: PropTypes.bool,
    isAddingNewBeneficalOwner: PropTypes.bool,
    topAccount: PropTypes.shape({
      isWhsLinkingActive: PropTypes.bool
    }),
    versionWindows: PropTypes.arrayOf(PropTypes.shape({}))
  };

  static defaultProps = {
    isGettingStatement: false,
    beneficiaryList: null,
    isUpdatingStatement: false,
    isStructureValid: false,
    permissions: null,
    settings: null,
    statement: null,
    structure: null,
    isOwnershipModalShown: false,
    changeOwnershipItem: null,
    isSavingStatement: false,
    statementPosition: null,
    validationResult: null,
    tierCollapseStructure: null,
    isDeletingRow: false,
    isAddingNewBeneficalOwner: false,
    topAccount: null,
    versionWindows: null
  };

  static getChapter3DescriptionByName = (name, displayNumber = true) => {
    const item = Chapter3Status.findByName(name);
    return (item && `${ item.description }${ displayNumber ? ' - ' : '' }${ displayNumber ? item.value : '' }`) || null;
  };

  static getChapter4DescriptionByName = (name, displayNumber = true) => {
    const item = Chapter4Status.findByName(name);
    return (item && `${ item.description }${ displayNumber ? ' - ' : '' }${ displayNumber ? item.value : '' }`) || null;
  };

  static getImyChapter3DescriptionByName = (name, displayNumber = true) => {
    const item = ImyChapter3Status.findByName(name);
    return (item && `${ item.description }${ displayNumber ? ' - ' : '' }${ displayNumber ? item.value : '' }`) || null;
  };

  static buildElementId = name => (name && name.replace(/[^a-zA-Z0-9-_]/g, '')) || '';

  static scrollToLastPosition = (position) => {
    window.scrollTo({
      top: position,
      behavior: 'smooth'
    });
  };

  static getIsDirectBeneficialOwner(beneficialOwner) {
    if (beneficialOwner.ownerDetails && !beneficialOwner.ownerDetails.allocationIncomeRate && beneficialOwner.recipients) {
      return false;
    }
    return true;
  }

  constructor(props) {
    super(props);
    this.state = {
      toggleState: {
        isTierExpanded: false,
      },
      isAllTiersExpanded: false,
      childAccountId: null,
      childOwnerGuid: null,
      childOwnerName: null,
      childParentId: null,
      missingFormsCount: 0,
      invalidFormsCount: 0,
      inReviewFormsCount: 0,
      missingAllocationCount: 0,
      rejectionsCount: 0,
      warningsCount: 0,
      selectedPills: {
        missingForms: false,
        invalidForms: false,
        inReviewForms: false,
        rejections: false,
        warnings: false,
        missingAllocation: false,
      }
    };

    this.handleChangeImyParentInputChange = this.handleChangeImyParentInputChange.bind(this);
  }

  componentDidMount() {
    const {
      match: { params: { accountId } },
      statementPosition
    } = this.props;

    this.getWithholdingStatement();
    this.setExpandedTier();
    this.calculateFilterCounts();

    if (statementPosition && statementPosition[accountId]) {
      const position = statementPosition[parseInt(accountId, 10)];
      setTimeout(() => (
        WhdStatement.scrollToLastPosition(position)
      ), 2000);
    }
  }

  componentDidUpdate(prevProps) {
    const {
      match: { params: { accountId } }, statement
    } = this.props;

    const {
      match: { params: { accountId: prevAccountId } }, statement: prevStatement
    } = prevProps;

    if (accountId !== prevAccountId) {
      this.getWithholdingStatement();
    }

    if (statement !== prevStatement) {
      this.setExpandedTier();
      this.calculateFilterCounts();
    }
  }

  onCloseWithholdingModal() {
    const { dispatch } = this.props;
    dispatch(hideAccountWithholdingModal());
  }

  onClosePooledRatesModal() {
    const { dispatch } = this.props;
    dispatch(hidePooledRatesModal());
  }

  setExpandedTier() {
    const { match: { params: { accountId } }, statement, tierCollapseStructure } = this.props;

    if (tierCollapseStructure && tierCollapseStructure[accountId] && statement && statement.intermediaryName) {
      this.setState({ isAllTiersExpanded: tierCollapseStructure[accountId].allTiersExpanded });
      if (tierCollapseStructure[accountId][statement.intermediaryName]) {
        this.setState({ toggleState: tierCollapseStructure[accountId][statement.intermediaryName] });
      }
    }
  }

  getWithholdingStatement() {
    const {
      dispatch,
      match: { params: { accountId, versionNumber } },
      permissions
    } = this.props;

    if (accountId > 0 && AccessControl.hasPermission(permissions, [ActivityPerms.multiTierMgrEditWhs, ActivityPerms.multiTierMgrViewWhs])) {
      dispatch(getWhdStatement([accountId], versionNumber));
    }
  }

  getTierRecipientCount() {
    const { structure } = this.props;
    return structure?.recipients ? structure?.recipients.filter(r => !r.recipients).length : 0;
  }

  getTierIntermediaryCount() {
    const { structure } = this.props;
    return structure?.recipients ? structure?.recipients.filter(r => r.recipients).length : 0;
  }

  getOwners = (recipient, level, structureLocator, index) => {
    const structureLocation = `${ structureLocator && '-' }${ index }`;
    const owners = [{ level, structureLocation, accountId: recipient.basicDetails.accountId, name: recipient.basicDetails.name }];

    const items = recipient.recipients
      .map((item, idx) => item.recipients && this.getOwners(item, level + 1, structureLocation, idx));

    return items.reduce((p, c) => {
      if (!c) {
        return p;
      }

      if (Array.isArray(c)) {
        return [...p, ...c];
      }

      return [...p, c];
    }, owners);
  };

  getParent = (accountId) => {
    const { structure } = this.props;

    if (!accountId || !structure || !structure.basicDetails || !structure.basicDetails.accountId) {
      return null;
    }

    if (structure.basicDetails.accountId === accountId) {
      return structure.basicDetails;
    }

    return this.findInRecipients(accountId, structure.recipients);
  };

  getStatementImyLayers = (accountId, parentAccountId) => {
    const { structure } = this.props;

    if (!structure || !structure.basicDetails) {
      return [];
    }

    const imys = this.getImyLayers(0, accountId, parentAccountId, structure);

    return imys || [];
  };

  getImyLayers = (level, accountId, parentAccountId, recipient) => {
    if (!recipient || !recipient.basicDetails || !Array.isArray(recipient.recipients)) {
      return null;
    }

    const relation = this.getImyRelation(recipient, accountId, parentAccountId);

    const imys = [{ level, accountId: recipient.basicDetails.accountId, guid: recipient.guid, name: recipient.basicDetails.name, relation }];

    const items = recipient.recipients
      .map(item => item.recipients && this.getImyLayers(level + 1, accountId, parentAccountId, item));

    return items.reduce((p, c) => {
      if (!c) {
        return p;
      }

      if (Array.isArray(c)) {
        return [...p, ...c];
      }

      return [...p, c];
    }, imys);
  };

  findInRecipients = (accountId, recipientList) => {
    if (!recipientList || !Array.isArray(recipientList) || recipientList.length === 0) {
      return null;
    }

    let recipient = recipientList && Array.isArray(recipientList) && recipientList.find(x => x.basicDetails && x.basicDetails.accountId === accountId);
    if (recipient) {
      return recipient.basicDetails;
    }

    recipient = recipientList.find(x => x.recipients && this.findInRecipients(accountId, x.recipients) !== null);

    return null;
  };

  // eslint-disable-next-line class-methods-use-this
  getImyRelation = (imyRecipient, accountId, parentAccountId) => {
    const parentAccount = imyRecipient && imyRecipient.basicDetails.accountId === parentAccountId && 'parent';
    const backupIdentifer = imyRecipient && imyRecipient.basicDetails && `${ imyRecipient.basicDetails.parentAccountId }-${ imyRecipient.guid || '' }`;
    const selfAccount = imyRecipient && accountId && (imyRecipient.basicDetails.accountId === accountId || backupIdentifer === accountId) && 'self';

    return parentAccount || selfAccount || null;
  };

  getToggleState = (rowIdentifier) => {
    const { statement, tierCollapseStructure, match: { params: { accountId: pAccountId } } } = this.props;
    if (statement && statement.intermediaryName && statement.intermediaryName === rowIdentifier) {
      const { toggleState } = this.state;
      return toggleState;
    }

    const storedState = tierCollapseStructure && tierCollapseStructure[pAccountId] && tierCollapseStructure[pAccountId][rowIdentifier];

    return storedState?.toggleState || {
      isTierExpanded: false,
      isOverrideTopTier: false,
      isAdditionalExpanded: false
    };
  };

  handleChangeImyParentInputChange = (event) => {
    const { changeParentData } = this.state;
    const name = event.target && event.target.name;
    const value = event.target && event.target.value;

    const newData = {
      ...changeParentData,
      [name]: value
    };

    this.setState({ changeParentData: newData });
  };

  updateToggledCollapsibleTiers = (rowIdentifier, toggleOptions) => {
    const { tierCollapseStructure, match: { params: { accountId: pAccountId } }, dispatch } = this.props;
    const { isAllTiersExpanded } = this.state;
    const updatedStucture = Object.assign({}, tierCollapseStructure) || { [pAccountId]: null };

    if (!updatedStucture[pAccountId]) {
      updatedStucture[pAccountId] = { allTiersExpanded: isAllTiersExpanded };
    } else {
      updatedStucture[pAccountId].allTiersExpanded = isAllTiersExpanded;
    }

    updatedStucture[pAccountId][rowIdentifier] = toggleOptions;

    dispatch(saveCollapsedTiers(updatedStucture));
  };

  updateTierSetExpanded = (rowIdentifier) => {
    const { tierCollapseStructure, match: { params: { accountId: pAccountId } }, dispatch } = this.props;
    const { isAllTiersExpanded } = this.state;
    const updatedStucture = Object.assign({}, tierCollapseStructure) || { [pAccountId]: null };

    if (!updatedStucture[pAccountId]) {
      updatedStucture[pAccountId] = { allTiersExpanded: isAllTiersExpanded };
    }

    const toggleState = updatedStucture[pAccountId][rowIdentifier] || { isAdditionalExpanded: false };
    toggleState.isTierExpanded = true;
    toggleState.isOverrideTopTier = true;
    updatedStucture[pAccountId][rowIdentifier] = toggleState;

    dispatch(saveCollapsedTiers(updatedStucture));
  };

  setCollapsibleTier = (rowIdentifier, isToggled) => {
    const { statement } = this.props;
    const toggleState = this.getToggleState(rowIdentifier);

    toggleState.isTierExpanded = isToggled;
    if (statement && statement.intermediaryName && statement.intermediaryName === rowIdentifier) {
      this.setState({ toggleState });
    }

    this.updateToggledCollapsibleTiers(rowIdentifier, toggleState);
  };

  toggleCollapseAllTiers = (isToggled) => {
    const { toggleState, isAllTiersExpanded } = (isToggled === true || isToggled === false) ? { isAllTiersExpanded: !isToggled } : this.state;
    const { statement } = this.props;
    toggleState.isTierExpanded = !isAllTiersExpanded;
    this.setState({ toggleState, isAllTiersExpanded: !isAllTiersExpanded });
    this.updateToggledCollapsibleTiers(statement.intermediaryName, toggleState);
  };

  toggleCollapseTier = () => {
    const { toggleState } = this.state;
    const { statement } = this.props;
    toggleState.isTierExpanded = !toggleState.isTierExpanded;
    this.setState({ toggleState });
    this.updateToggledCollapsibleTiers(statement.intermediaryName, toggleState);
  };

  expandTier = () => {
    const { toggleState } = this.state;
    const { statement } = this.props;

    toggleState.isTierExpanded = true;

    this.setState({ toggleState });
    this.updateToggledCollapsibleTiers(statement.intermediaryName, toggleState);
  };

  getOwnersOptionsList = () => {
    const { beneficiaryList } = this.props;

    if (!beneficiaryList) {
      return [];
    }

    return beneficiaryList.map((owner) => {
      const parent = (owner.parents && owner.parents.length > 0 && ` - (${ owner.parents[owner.parents.length - 1] })`) || '';
      return {
        display: `${ owner.name ?? owner.accountName }${ parent }`,
        key: owner.guid || owner.name || owner.accountName,
        value: WhdStatement.buildElementId(owner.guid || owner.name || owner.accountName),
        searchValue: owner.name ?? owner.accountName,
        parentTier: owner.parents && owner.parents.length > 0 && owner.parents[owner.parents.length - 1],
        parentIdentifier: owner.parentIdentifier,
        isIMY: owner.isIMY
      };
    });
  };

  onChangeParentCancel = () => {
    this.setState({ isChangeParentOpen: false });
  };

  onChangeParentSubmit = () => {
    const { dispatch, structure: { basicDetails: { accountId } } } = this.props;
    const { changeParentData, childAccountId, childOwnerGuid, childParentId } = this.state;

    const changeParentDetails = {
      ...changeParentData,
      childAccountId,
      childOwnerGuid,
      currentParentAccountId: childParentId
    };

    this.setState({ isChangeParentOpen: false });

    dispatch(changeImyTierParent(accountId, changeParentDetails));
  };

  openChangeParentModal = (guid, item, index) => {
    const { isChangeParentOpen } = this.state;

    if (!isChangeParentOpen) {
      this.setState({ isChangeParentOpen: true });
    }

    this.setState({ childAccountId: item.accountId });
    this.setState({ childOwnerGuid: guid });
    this.setState({ childOwnerName: item.name });
    this.setState({ childParentId: item.parentAccountId });

    const currentParent = this.getParent(item.parentAccountId);

    this.setState({ childParentName: (currentParent && currentParent.name) || null });
  };

  tierHasValidationErrors = () => {
    const { structure, validationResult } = this.props;
    if (!structure.recipients || !validationResult?.lines || validationResult.lines.length === 0) {
      return false;
    }

    return structure.recipients.some((recipient) => {
      const lineNo = recipient && (recipient.lineNo || (recipient.ownerDetails && recipient.ownerDetails.lineNo > 0 && recipient.ownerDetails.lineNo));
      const lineValidation = lineNo > 0 && validationResult.lines.find(result => result.lineNumber === lineNo);

      return lineValidation
        && (ValidationInspector.hasRejections(lineValidation)
          || ValidationInspector.hasWarnings(lineValidation));
    });
  };

  reAssignItem = () => {
    const { dispatch, changeOwnershipItem } = this.props;
    if (changeOwnershipItem != null && changeOwnershipItem.id != null) {
      dispatch(takeQueueItem('form', changeOwnershipItem.id, changeOwnershipItem.accountId));
      dispatch(hideOwnershipModal());
    }
  };

  openAccountWithholdingModal = (formId) => {
    const { dispatch } = this.props;
    dispatch(showAccountWithholdingModal(formId, null, null));
  };

  openPooledRatesModal = (guid, rates) => {
    const { dispatch } = this.props;
    dispatch(showPooledRatesModal(guid, rates));
  };

  togglePill = (pillName) => {
    this.setState(prevState => ({
      selectedPills: {
        ...prevState.selectedPills,
        [pillName]: !prevState.selectedPills[pillName],
      }
    }));
  };

  getPillClass = (count, pillName) => {
    const { selectedPills } = this.state;
    return `${ count === 0 ? 'grey' : '' } ${ selectedPills[pillName] ? 'selected' : '' }`;
  };

  extractAllRecipients() {
    const { structure } = this.props;
    const allRecipients = [];

    const flatten = (recipients) => {
      recipients.forEach((r) => {
        if (r.basicDetails) {
          allRecipients.push(r);
        }

        if (Array.isArray(r.recipients)) {
          flatten(r.recipients);
        }
      });
    };

    if (structure?.recipients) {
      flatten(structure.recipients);
    }

    return allRecipients;
  }

  calculateFilterCounts() {
    const { structure, validationResult } = this.props;
    if (!structure?.recipients) return;

    const all = this.extractAllRecipients(structure.recipients || []);

    let missingFormsCount = 0;
    let invalidFormsCount = 0;
    let inReviewFormsCount = 0;
    let missingAllocationCount = 0;
    let rejectionsCount = 0;
    let warningsCount = 0;

    all.forEach((r) => {
      const {
        isFormMissing,
        isFormInvalid,
        isFormInReviewStage,
        isBoMissingAllocation,
        warningCount: boWarningCount,
        rejectionCount: boRejectionCount
      } = BeneficialOwner.checkRecipientFilterMatches(r, validationResult);

      if (isFormMissing) {
        missingFormsCount += 1;
      }
      if (isFormInvalid) {
        invalidFormsCount += 1;
      }
      if (isFormInReviewStage) {
        inReviewFormsCount += 1;
      }
      if (isBoMissingAllocation) {
        missingAllocationCount += 1;
      }
      if (boWarningCount > 0) {
        warningsCount += 1;
      }
      if (boRejectionCount > 0) {
        rejectionsCount += 1;
      }
    });

    this.setState({
      missingFormsCount,
      invalidFormsCount,
      inReviewFormsCount,
      missingAllocationCount,
      rejectionsCount,
      warningsCount
    });
  }

  openAddFormModal(item) {
    const { dispatch } = this.props;
    dispatch(showAddFormModal(item, null));
  }

  addForms(parentForm, values, onlineForm) {
    const { dispatch } = this.props;
    dispatch(addForm(parentForm, values, onlineForm));
  }

  closeAddFormModal(item) {
    const { dispatch, match: { params: { accountId } } } = this.props;
    dispatch(hideAddFormModal(item));
    dispatch(getWhdStatement([accountId]));
  }

  closeFromOwnershipModal() {
    const { dispatch } = this.props;
    dispatch(hideOwnershipModal());
  }

  continueFromOwnershipModal() {
    const { dispatch, changeOwnershipItem } = this.props;
    dispatch(hideOwnershipModal());
    let redirectUrl = `/forms/${ changeOwnershipItem.formType.toLowerCase() }/${ changeOwnershipItem.id }`;
    if (changeOwnershipItem.accountId) {
      redirectUrl = `${ redirectUrl }?account=${ changeOwnershipItem.accountId }`;
    }
    history.push(redirectUrl);
  }

  addBeneficialOwner(accountId, parentGuid, isImyLayer, isPooled, preventExpand, ownerId) {
    const { dispatch } = this.props;
    const rowIdentifier = ownerId || parentGuid;
    if (!preventExpand && rowIdentifier) {
      this.updateTierSetExpanded(rowIdentifier);
    }
    dispatch(addNewBeneficialOwner(accountId, parentGuid, isImyLayer, isPooled, rowIdentifier));
    setTimeout(() => {
      dispatch(setStatementPosition(accountId, window.pageYOffset));
    }, 1000);
  }

  isJointAccount() {
    const { statement } = this.props;
    return statement?.jointAccountElection?.toLowerCase() === 'yes';
  }

  render() {
    const {
      isGettingStatement,
      isUpdatingStatement,
      isStructureValid,
      permissions,
      settings,
      statement,
      structure,
      validationResult,
      match: { params: { accountId: pAccountId, versionNumber } },
      isOwnershipModalShown,
      changeOwnershipItem,
      isAccountWithholdingShown,
      isAddFormModalShown,
      isSavingStatement,
      tierCollapseStructure,
      isPooledRatesModalShown,
      isDeletingRow,
      isAddingNewBeneficalOwner,
      dispatch,
      topAccount,
      versionWindows
    } = this.props;

    const { toggleState: { isTierExpanded }, isAllTiersExpanded, isChangeParentOpen, childAccountId, childOwnerName, childOwnerGuid, childParentId, childParentName,
      missingFormsCount, invalidFormsCount, inReviewFormsCount, missingAllocationCount, rejectionsCount, warningsCount, selectedPills } = this.state;

    const hasPermission = AccessControl.hasPermission(permissions, [ActivityPerms.multiTierMgrEditWhs, ActivityPerms.multiTierMgrViewWhs]);
    const canEditPermission = AccessControl.hasPermission(permissions, [ActivityPerms.multiTierMgrEditWhs]);
    const canUploadPermission = AccessControl.hasPermission(permissions, [ActivityPerms.scannedUpload, ActivityPerms.scannedWhdStmtBoUpload], true);
    const isLicensed = settings.showMultiTierManager;
    const isPooled = statement && statement.type === StatementTypes.PooledWhdStatement;
    const accountId = parseInt(pAccountId, 10) || null;
    const showingVersion = versionNumber !== null && typeof versionNumber !== 'undefined';
    const imyLayerOptions = isChangeParentOpen && this.getStatementImyLayers(childAccountId || `${ childParentId }-${ childOwnerGuid }`, childParentId)
      .map(x => <option
        className={ x.relation ? 'disabled italic' : '' }
        disabled={ !!x.relation }
        key={ `${ x.accountId }|${ x.guid }` }
        value={ `${ x.accountId || '' }|${ x.guid || '' }` }
      >{ x.level > 0 && [...Array(x.level - 1), '-\u00A0\u00A0\u00A0'].map((e, i) => e || '\u00A0\u00A0\u00A0') }{ x.name }{ x.relation && `\u00A0\u00A0\u00A0(${ x.relation })` }</option>);
    const withholdingStruct = structure;
    const tierRecipients = this.getTierRecipientCount();
    const tierIntermediaries = this.getTierIntermediaryCount();
    const collapseStructure = tierCollapseStructure ? Object.assign({}, tierCollapseStructure[pAccountId]) : null;

    const buildImyTierObject = (item) => {
      if (!item) {
        return null;
      }
      const formObject = {
        accountId: item.accountId,
        accountName: item.name,
        accountNumber: item.accountNumber,
        customerDbId: item.customerDbId,
        customerId: item.customerIdentifier,
        customerName: item.customerName,
      };

      return formObject;
    };

    const ownerList = this.getOwnersOptionsList();

    const scrollToItem = (item) => {
      const id = WhdStatement.buildElementId(item.value);
      if (item.parentIdentifier && item.parentIdentifier !== statement.intermediaryName && !item.isIMY) {
        this.updateTierSetExpanded(item.parentIdentifier);
      } else if (item.parentIdentifier && item.parentIdentifier === statement.intermediaryName && !item.isIMY) {
        this.expandTier();
      }

      setTimeout(() => {
        const element = document.getElementById(id);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth' });
          setTimeout(() => {
            dispatch(setStatementPosition(accountId, window.pageYOffset));
          }, 1000);
        }
      }, 100);
    };

    const versionExpired = statement && statement.effectiveTo && moment(statement.effectiveTo).isBefore(moment(), 'day');
    const versionExpiring = statement && statement.effectiveTo && moment(statement.effectiveTo).isSame(moment(), 'day');
    const versionCurrent = statement && statement.effectiveTo && moment(statement.effectiveTo).isAfter(moment(), 'day');
    const versionOpenEnded = statement && (!statement.effectiveTo || statement.effectiveTo.length === 0);

    const missingForm = structure?.basicDetails?.formId === null;
    const invalidForm = !missingForm && (structure?.basicDetails?.formIsValid === false || structure?.basicDetails?.formIsValid == null);
    const formInReview = structure?.basicDetails?.workflowStage >= 4 && structure?.basicDetails?.workflowStage <= 9;
    const formComplete = structure?.basicDetails?.workflowStage === 100;
    const hasRejections = validationResult?.rejections?.length > 0;
    const hasWarnings = validationResult?.warnings?.length > 0;

    return (
      <div className="digital-whd-statement" key="imy" style={ { height: '100%' } } onScroll={ e => this.scroll(e) }>
        { (!isLicensed || !hasPermission) && <div className="access-error">
          { !isLicensed && <h2>Your licence does not grant access to Multi-Tier Management</h2> }
          { isLicensed && !hasPermission && <h2>You do not have permission to access the Digital Withholding Statement</h2> }
          <p>Please contact your administrator for more details</p>
        </div> }
        { isLicensed && hasPermission && <React.Fragment>
          <h1>Digital Withholding Statement</h1>
          {versionNumber && versionExpiring && <Banner
            mainText="Withholding Statement: Published (expires today)"
            subText="Withholding Rates as of the Effective To date"
            className="toast-red"
          />}
          {versionNumber && versionExpired && <Banner
            mainText={ `Withholding Statement: Expired (${ statement && statement.effectiveTo })` }
            subText="Withholding Rates as of the Effective To date"
            className="toast-red"
          />}
          {versionNumber && versionCurrent && <Banner
            mainText={ `Withholding Statement: Published (expires ${ statement && statement.effectiveTo })` }
            className="toast-blue"
          />}
          {versionNumber && versionOpenEnded && <Banner
            mainText="Withholding Statement: Published (no expiry)"
            className="toast-blue"
          />}
          {!versionNumber && <Banner
            mainText="Withholding Statement: Draft"
            className="toast-grey"
          />}
          { isGettingStatement && <LoadingWrapper loaded={ !isGettingStatement } message="Please wait while Multi-Tier Manager fetches the Withholding Statement" /> }
          { !isGettingStatement && !statement && <NotFoundPage caption="Withholding Statement Not Found" /> }
          { statement && <div>
            <div className="columns">
              <div className="col-8 mb-2">
                <IntermediaryDetailsPanel
                  accountId={ pAccountId }
                  getChapter3DescriptionByName={ WhdStatement.getImyChapter3DescriptionByName }
                  getChapter4DescriptionByName={ WhdStatement.getChapter4DescriptionByName }
                  statement={ statement }
                  dispatch={ dispatch }
                  permissions={ permissions }
                  validationResult={ validationResult || null }
                  showingVersion={ showingVersion }
                />
              </div>
              <div className="col-4 mb-2">
                <StatementOptionsPanel
                  accountId={ pAccountId }
                  statement={ statement }
                  structure={ structure }
                  dispatch={ dispatch }
                  permissions={ permissions }
                  validationResult={ validationResult || null }
                  showingVersion={ showingVersion }
                />
              </div>
              <div className="col-12 my-2">
                <StatementActionsPanel
                  accountId={ accountId }
                  statement={ statement }
                  structure={ structure }
                  validationResult={ validationResult }
                  expandTier={ tierName => this.setCollapsibleTier(tierName, true) }
                  className="whs-top-level"
                  statementVersion={ versionNumber }
                  versionWindows={ versionWindows }
                />
              </div>

              <div className="col-3 mt-3rem mb-2rem">
                <SimpleAutoComplete
                  options={ ownerList }
                  onClickAction={ scrollToItem }
                  placeholder="Search Statement"
                />
              </div>
              <div className="col-9 mt-3rem mb-2rem">
                <div className="filter-container">
                  <strong className="ml-1rem mr-1rem">Forms:</strong>
                  <button
                    type="button"
                    disabled={ missingFormsCount === 0 }
                    className={ `status-pill red ${ this.getPillClass(missingFormsCount, 'missingForms') }` }
                    onClick={ () => this.togglePill('missingForms') }
                  >
                    {missingFormsCount} Missing
                  </button>
                  <button
                    type="button"
                    disabled={ invalidFormsCount === 0 }
                    className={ `status-pill red ${ this.getPillClass(invalidFormsCount, 'invalidForms') }` }
                    onClick={ () => this.togglePill('invalidForms') }
                  >
                    {invalidFormsCount} Invalid
                  </button>
                  <button
                    type="button"
                    disabled={ inReviewFormsCount === 0 }
                    className={ `status-pill orange ${ this.getPillClass(inReviewFormsCount, 'inReviewForms') }` }
                    onClick={ () => this.togglePill('inReviewForms') }
                  >
                    {inReviewFormsCount} In Review
                  </button>
                  <strong className="ml-1rem mr-1rem">Statement:</strong>
                  <button
                    type="button"
                    disabled={ rejectionsCount === 0 }
                    className={ `status-pill red ${ this.getPillClass(rejectionsCount, 'rejections') }` }
                    onClick={ () => this.togglePill('rejections') }
                  >
                    {rejectionsCount} Rejection(s)
                  </button>
                  <button
                    type="button"
                    disabled={ warningsCount === 0 }
                    className={ `status-pill orange ${ this.getPillClass(warningsCount, 'warnings') }` }
                    onClick={ () => this.togglePill('warnings') }
                  >
                    {warningsCount} Warning(s)
                  </button>
                  <button
                    type="button"
                    disabled={ missingAllocationCount === 0 }
                    className={ `status-pill orange ${ this.getPillClass(missingAllocationCount, 'missingAllocation') }` }
                    onClick={ () => this.togglePill('missingAllocation') }
                  >
                    {missingAllocationCount} Allocation % Missing
                  </button>
                  <button
                    type="button"
                    onClick={ this.toggleCollapseAllTiers }
                    className="btn"
                    data-testid={ isAllTiersExpanded ? 'btn-collapse-all-tiers' : 'btn-open-all-tiers' }
                  ><span className="icon icon-resize-vert" /> { isAllTiersExpanded ? 'Collapse' : 'Open' } All Tiers</button>
                </div>
              </div>

              <div id={ WhdStatement.buildElementId(statement.intermediaryName) } className="col-12 pt-2 beneficiary-owner">
                <div className="statement-breadcrumbs">
                  <div className="breadcrumbs">
                    / { statement.intermediaryName }
                  </div>
                  <div className="button-row">
                    <button type="button" onClick={ this.toggleCollapseTier } className="btn link-button"><span className="icon icon-resize-vert" />{ isTierExpanded ? 'Collapse' : 'Open' } Tier</button>
                  </div>
                </div>
                <div className={ `panel mb-2 imy-card${ (this.tierHasValidationErrors() && ' validation-highlight') || '' }` } data-testid={ removeInvalidHtmlChars(`${ statement.intermediaryName }`).toLowerCase() }>
                  <div className="columns">
                    <div className="col-12">
                      <div className="columns imy-layer">
                        <div className="col-6 align-vertical-middle">
                          <div className="owner-header">
                            <h2 className="inline mr-2rem value" id={ `${ WhdStatement.buildElementId(statement.intermediaryName) }_bo-header` }>{ statement.intermediaryName }</h2>
                            { missingForm && (<span className="status-pill red flat">Form Missing</span>) }
                            { formInReview && (<span className="status-pill orange flat">Form In Review</span>) }
                            { formComplete && (<span className="status-pill grey flat">Form Complete</span>) }
                            { structure?.basicDetails?.formId != null && (
                              invalidForm ? (
                                <span className="status-pill red flat">Form Invalid</span>
                              ) : (
                                <span className="status-pill grey flat">Form Valid</span>
                              )
                            ) }
                            { hasRejections > 0 && (<button type="button" className="status-pill red flat">Rejection(s)</button>) }
                            { hasWarnings > 0 && (<button type="button" className="status-pill orange flat">Warning(s)</button>) }
                          </div>

                          <div className="owner-header value mr-2">{ tierRecipients } { isPooled ? 'Pool' : 'Owner' }{ tierRecipients === 0 || tierRecipients > 1 ? 's' : '' }</div>
                          { !isPooled && <div className="owner-header value">/ { tierIntermediaries } Intermediar{ tierIntermediaries === 0 || tierIntermediaries > 1 ? 'ies' : 'y' }</div> }
                        </div>
                        <div className="col-2" />
                        <div className="col-4 align-vertical-center">
                          {!settings.selectedClient.isPiiProtectionEnabled && !isPooled && canUploadPermission && !showingVersion && <div className="col-4 align-vertical-center">
                            <button
                              type="button"
                              className="btn btn-secondary"
                              onClick={ () => this.openAddFormModal(buildImyTierObject(withholdingStruct.basicDetails)) }
                              data-testid="btn-upload-forms"
                            >
                              <i className="icon icon-2x icon-upload mr-2" />Upload Form(s)
                            </button>
                          </div> }
                          { canEditPermission && !showingVersion && <div className="col-4 align-vertical-center">
                            <button
                              type="button"
                              className="btn btn-secondary"
                              disabled={ isAddingNewBeneficalOwner }
                              onClick={ () => this.addBeneficialOwner(accountId, null, false, isPooled, false, statement.intermediaryName) }
                              data-testid={ isPooled ? 'btn-add-pool' : 'btn-add-beneficial-owner' }
                            >
                              <i className="icon icon-2x icon-plus mr-2" />{ isPooled ? 'Add Pool' : 'Add Owner' }
                            </button>
                          </div> }
                          { !isPooled && canEditPermission && !showingVersion && <div className="col-4 align-vertical-center">
                            <button
                              type="button"
                              className="btn btn-secondary"
                              disabled={ isAddingNewBeneficalOwner }
                              onClick={ () => this.addBeneficialOwner(accountId, null, true, isPooled, true, statement.intermediaryName) }
                              data-testid="btn-add-imy-tier"
                            >
                              <i className="icon icon-2x icon-plus mr-2" />Add IMY Tier
                            </button>
                          </div> }
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                { structure.recipients && structure.recipients.map((x, idx) => <BeneficialOwner
                  key={ x.guid || (x.ownerDetails && x.ownerDetails.guid) }
                  beneficialOwner={ x }
                  selectedPills={ selectedPills }
                  decimalPrecision={ statement.decimalPlacePrecision ?? 8 }
                  getChapter3DescriptionByName={ WhdStatement.getChapter3DescriptionByName }
                  getChapter4DescriptionByName={ WhdStatement.getChapter4DescriptionByName }
                  parents={ [statement.intermediaryName] }
                  statementType={ statement.type }
                  isJointAccount={ this.isJointAccount() }
                  isTopTierExpanded={ isAllTiersExpanded }
                  isParentTierExpanded={ isTierExpanded }
                  openChangeParentModal={ this.openChangeParentModal }
                  idx={ idx }
                  dispatch={ dispatch }
                  permissions={ permissions }
                  openAccountWithholdingModal={ formId => this.openAccountWithholdingModal(formId) }
                  openAddFormModal={ node => this.openAddFormModal(node) }
                  addBeneficialOwner={ (id, parentGuid, isImyLayer, preventExpand) => this.addBeneficialOwner(id, parentGuid, isImyLayer, isPooled, preventExpand, parentGuid) }
                  topAccountId={ accountId }
                  isSavingStatement={ isSavingStatement }
                  updateToggledCollapsibleTiers={ (rowIdentifier, toggleState) => this.updateToggledCollapsibleTiers(rowIdentifier, toggleState) }
                  tierCollapseStructure={ collapseStructure }
                  validationResult={ validationResult }
                  validationLineNo={ (x.ownerDetails || x).lineNo }
                  openPooledRatesModal={ (guid, rates) => this.openPooledRatesModal(guid, rates) }
                  parentAccountId={ accountId }
                  isDeletingRow={ isDeletingRow }
                  isStructureValid={ isStructureValid }
                  isAddingNewBeneficalOwner={ isAddingNewBeneficalOwner }
                  statementVersion={ versionNumber }
                  parentTierTotalAllocation={ 100 }
                  hideTierAllocationValue={ WhdStatement.getIsDirectBeneficialOwner(x) }
                />) }
              </div>
            </div>
            <ModalWrapper title="Saving changes..." headerClose={ false } showFooter={ false } isOpen={ isUpdatingStatement }>
              <div className="whd-statement-modal-saving-body"><LoadingWrapper loaded={ !isUpdatingStatement } message="Please wait while the changes are saved" /></div>
            </ModalWrapper>
          </div> }
          <ModalWrapper title="Change Parent" className="change-parent-modal" isOpen={ isChangeParentOpen } onCancel={ this.onChangeParentCancel } onConfirm={ this.onChangeParentSubmit } shouldCloseOnOverlayClick>
            <p>Change Parent for <strong>{ childOwnerName }</strong> ({ childAccountId })</p>
            <p className="mt-2">Current Parent: { childParentName } ({ childParentId })</p>
            <div className="form-group">
              <select name="newParentIdentifier" className="form-select" defaultValue="" onChange={ this.handleChangeImyParentInputChange }>
                <option value="" disabled>Select new parent</option>
                { imyLayerOptions }
              </select>
            </div>
          </ModalWrapper>
        </React.Fragment> }
        { changeOwnershipItem != null && <ChangeOwnershipModal
          isShown={ isOwnershipModalShown }
          onAssign={ () => this.reAssignItem() }
          onContinueWithoutAssign={ () => this.continueFromOwnershipModal() }
          onCancel={ () => this.closeFromOwnershipModal() }
          workflowRecord={ changeOwnershipItem }
        /> }
        <WithholdingRatesModal
          isShown={ isAccountWithholdingShown }
          onClose={ () => this.onCloseWithholdingModal() }
        />
        {!settings.selectedClient.isPiiProtectionEnabled && <AddFormModal
          isShown={ isAddFormModalShown }
          onConfirmUpload={ (imyTier, values) => this.addForms(imyTier, values, false) }
          onCancel={ () => this.closeAddFormModal() }
        />}
        { isPooled && <PooledRatesModal
          structureId={ structure?.basicDetails?.formId }
          accountId={ accountId }
          isShown={ isPooledRatesModalShown }
          statementVersion={ versionNumber }
          onClose={ () => this.onClosePooledRatesModal() }
          permissions={ permissions }
        /> }
        { accountId > 0 && topAccount && topAccount.isWhsLinkingActive
          && <LinkWhsAccountModal
            key="LinkWhsAccount"
            isShown={ topAccount.isWhsLinkingActive }
            accountId={ accountId }
            reloadOnComplete={ false }
          />}
      </div>
    );
  }
}

function mapStateToProps(state) {
  const {
    auth: {
      permissions,
      settings
    },
    withholdingStatement: {
      beneficiaryList,
      isGettingStatement,
      isSavingStatement,
      isUpdatingStatement,
      statement,
      structure,
      statementPosition,
      tierCollapseStructure,
      validationResult,
      isDeletingRow,
      isStructureValid,
      isAddingNewBeneficalOwner,
      topAccount,
      versionWindows
    },
    forms: {
      isAccountWithholdingShown,
      isAddFormModalShown,
      isPooledRatesModalShown
    },
    workflow: {
      isOwnershipModalShown,
      changeOwnershipItem
    }
  } = state;

  return {
    isGettingStatement,
    beneficiaryList,
    isSavingStatement,
    isUpdatingStatement,
    permissions,
    settings,
    statement,
    structure,
    statementPosition,
    isOwnershipModalShown,
    changeOwnershipItem,
    isAccountWithholdingShown,
    isAddFormModalShown,
    validationResult,
    tierCollapseStructure,
    isPooledRatesModalShown,
    isDeletingRow,
    isStructureValid,
    isAddingNewBeneficalOwner,
    topAccount,
    versionWindows
  };
}

const mapDispatchToProps = dispatch => ({ dispatch });

export default withRouter(connect(
  mapStateToProps,
  mapDispatchToProps
)(withContainerErrorBoundary(WhdStatement, 'WhdStatement')));
